import { useEffect, useId, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "./Button";

const overlayStack: string[] = [];
let bodyOverflowBeforeOverlay = "";

function useEscape(open: boolean, onClose: () => void): string {
  const overlayId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    if (overlayStack.length === 0) bodyOverflowBeforeOverlay = document.body.style.overflow;
    overlayStack.push(overlayId);
    document.body.style.overflow = "hidden";

    const handler = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      const dialogs = document.querySelectorAll<HTMLElement>("[data-overlay-id]");
      if (dialogs.item(dialogs.length - 1)?.dataset.overlayId !== overlayId) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onCloseRef.current();
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      const index = overlayStack.indexOf(overlayId);
      if (index >= 0) overlayStack.splice(index, 1);
      if (overlayStack.length === 0) document.body.style.overflow = bodyOverflowBeforeOverlay;
    };
  }, [open, overlayId]);
  return overlayId;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: "sm" | "md" | "lg" | "xl" | "2xl";
}) {
  const overlayId = useEscape(open, onClose);
  const titleId = useId();
  if (!open) return null;

  const max = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl", "2xl": "max-w-5xl" }[width];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div
        aria-hidden="true"
        className="absolute inset-0 animate-fade bg-ink/45 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className={`relative flex max-h-[90dvh] w-full ${max} animate-pop flex-col overflow-hidden rounded-lg border border-rule bg-raised shadow-pop`}
        data-overlay-id={overlayId}
        role="dialog"
      >
        <header className="flex items-start justify-between gap-4 px-6 pt-5 pb-4">
          <div className="min-w-0">
            <h2 className="font-title text-lg leading-snug font-semibold text-ink" id={titleId}>
              {title}
            </h2>
            {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
          </div>
          <CloseButton onClose={onClose} />
        </header>
        <div className="flex-1 overflow-y-auto px-6 pb-5">{children}</div>
        {footer ? (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-rule bg-ink/[0.02] px-6 py-3.5">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}

export function SlideOver({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const overlayId = useEscape(open, onClose);
  const titleId = useId();
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        aria-hidden="true"
        className="absolute inset-0 animate-fade bg-ink/45 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <aside
        aria-labelledby={titleId}
        aria-modal="true"
        className="absolute inset-y-0 right-0 flex w-full max-w-md animate-slide-in flex-col border-l border-rule bg-raised shadow-pop"
        data-overlay-id={overlayId}
        role="dialog"
      >
        <header className="flex items-center justify-between gap-4 border-b border-rule px-6 py-4">
          <h2 className="font-title text-lg font-semibold text-ink" id={titleId}>
            {title}
          </h2>
          <CloseButton onClose={onClose} />
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer ? (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-rule bg-ink/[0.02] px-6 py-3.5">
            {footer}
          </footer>
        ) : null}
      </aside>
    </div>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      aria-label="Close"
      className="-mr-2 grid size-8 shrink-0 place-items-center rounded-md text-muted transition-colors hover:bg-ink/[0.06] hover:text-ink"
      onClick={onClose}
      type="button"
    >
      <X aria-hidden="true" className="size-4" />
    </button>
  );
}

/** Destructive actions always name the thing being destroyed. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Delete",
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  loading?: boolean;
}) {
  return (
    <Modal
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button loading={loading} onClick={onConfirm} variant="danger">
            {confirmLabel}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title={title}
      width="sm"
    >
      <div className="text-sm text-ink">{message}</div>
    </Modal>
  );
}
